import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { MyNotificationsViewModel } from '@/app/proxy/proxies';
import { NotificationService } from '@/app/core/services/notification.service';
import { IconsModule } from '@/app/components/icons/icons.module';

@Component({
  selector: 'dk-notification-toast',
  standalone: true,
  imports: [CommonModule, IconsModule],
  templateUrl: './notification-toast.component.html',
  styleUrl: './notification-toast.component.less',
})
export class NotificationToastComponent implements OnInit, OnDestroy {
  activeNotifications: MyNotificationsViewModel[] = [];
  showMoreToast = false;
  private subscription?: Subscription;
  private autoCloseTimers: Map<string, number> = new Map();
  private showMoreTimer: number | null = null;
  private failedIconIds = new Set<string>();
  private readonly MAX_VISIBLE_TOASTS = 2;
  private readonly TOAST_AUTO_CLOSE_MS = 5000;

  constructor(
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeToastManagement();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.clearShowMoreTimer();
    this.autoCloseTimers.forEach((timer) => clearTimeout(timer));
    this.autoCloseTimers.clear();
  }

  private initializeToastManagement(): void {
    this.subscription = this.notificationService.realtimeNotifications$.subscribe((notification) => {
      this.showToast(notification);
    });
  }

  private showToast(notification: MyNotificationsViewModel): void {
    // Don't show duplicate toasts
    if (this.activeNotifications.some((n) => n.id === notification.id)) {
      return;
    }

    if (this.activeNotifications.length >= this.MAX_VISIBLE_TOASTS) {
      this.showMoreToast = true;
      this.scheduleShowMoreAutoClose();
      return;
    }

    this.activeNotifications.push(notification);

    const timer = window.setTimeout(() => {
      this.closeToast(notification);
    }, this.TOAST_AUTO_CLOSE_MS);

    this.autoCloseTimers.set(notification.id, timer);
  }

  public closeToast(notification: MyNotificationsViewModel): void {
    this.activeNotifications = this.activeNotifications.filter((n) => n.id !== notification.id);

    const timer = this.autoCloseTimers.get(notification.id);
    if (timer) {
      clearTimeout(timer);
      this.autoCloseTimers.delete(notification.id);
    }

    if (this.overflowCount === 0) {
      this.showMoreToast = false;
    }
  }

  public openNotificationUrl(notification: MyNotificationsViewModel): void {
    if (notification.url) {
      window.open(notification.url, '_self');
    }

    // Mark notification as seen when clicked
    if (!notification.isSeen) {
      this.notificationService.markAsSeen(notification).subscribe();
    }

    this.closeToast(notification);
  }

  public closeAllToasts(): void {
    this.activeNotifications.forEach((notification) => {
      this.closeToast(notification);
    });
  }

  public getActiveToasts(): MyNotificationsViewModel[] {
    return [...this.activeNotifications];
  }

  get overflowCount(): number {
    return Math.max(0, this.notificationService.getUnreadCount() - this.activeNotifications.length);
  }

  get overflowCountLabel(): string {
    const count = this.overflowCount;
    return count > 99 ? '99+' : `${count}`;
  }

  // Helper methods for template
  getFormattedTime(notification: MyNotificationsViewModel): string {
    return notification.createdAt.toJSDate().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getIconClass(notification: MyNotificationsViewModel): string {
    return notification.icon || 'ph-fill ph-bell';
  }

  onNotificationIconError(notificationId?: string): void {
    if (notificationId) {
      this.failedIconIds.add(notificationId);
      this.cdr.detectChanges();
    }
  }

  hasIconError(notificationId?: string): boolean {
    return !!notificationId && this.failedIconIds.has(notificationId);
  }

  public onShowMoreClick(): void {
    this.closeShowMoreToast();
    window.dispatchEvent(new CustomEvent('open-notifications-panel'));
  }

  public closeShowMoreToast(): void {
    this.clearShowMoreTimer();
    this.showMoreToast = false;
  }

  private scheduleShowMoreAutoClose(): void {
    this.clearShowMoreTimer();
    this.showMoreTimer = window.setTimeout(() => {
      this.closeShowMoreToast();
    }, this.TOAST_AUTO_CLOSE_MS);
  }

  private clearShowMoreTimer(): void {
    if (this.showMoreTimer !== null) {
      clearTimeout(this.showMoreTimer);
      this.showMoreTimer = null;
    }
  }
}
